import {
  evaluateSettingAvailability,
  isSettingAvailabilitySearchVisible,
  shouldShowSettingsForAvailability,
} from "./availability";
import {
  describeSettingsCatalogCoverage,
  shouldShortCircuitCatalogSearch,
  type SettingsCatalogCoverage,
} from "./coverage";
import {
  canReadSetting,
  getSettingCatalogDefinitionsForGroup,
  SettingCatalogGroups,
} from "./index";
import {
  getReturnsSettingsLocations,
  type ReturnsSettingsLocations,
} from "./returns-new-settings";
import { createValueSemanticsSearchText } from "./search-context";
import {
  sanitizeSearchLimit,
  searchWeightedDocuments,
  stringifySearchContext,
  toSearchRelevanceScore,
} from "./search-index";
import type {
  SettingAvailabilityContext,
  SettingAvailabilityState,
  SettingDefinition,
  SettingGroupChatSurface,
  SettingGroupDefinition,
  SettingGroupFullPageTarget,
  SettingProductArea,
  SettingVisibility,
} from "./types";

const DEFAULT_SETTINGS_SEARCH_VIEWER_VISIBILITY = "merchant";
const MAX_MATCHED_SETTINGS_PER_GROUP = 5;
/** Exported so catalog-health checks index the catalog exactly the way the
 * agent does — a reimplementation would drift and stop proving anything. */
export const SETTINGS_SEARCH_KEYS = [
  { name: "id", weight: 3 },
  { name: "title", weight: 4 },
  { name: "description", weight: 2 },
  { name: "synonyms", weight: 5 },
  { name: "productArea", weight: 1 },
  { name: "groupTitle", weight: 2 },
  { name: "groupDescription", weight: 1 },
  { name: "groupSynonyms", weight: 4 },
  { name: "valueSemantics", weight: 1 },
  { name: "changeGuidance", weight: 1 },
  { name: "dependencies", weight: 1 },
  { name: "valueSource", weight: 1.5 },
] as const;

/** A merchant typing a setting's exact name must land on it. Order is
 *  precedence: a setting's own label beats another setting's synonym. */
export const SETTINGS_SEARCH_EXACT_MATCH_KEYS = ["title", "synonyms"] as const;

export interface SettingsSearchMatchedSetting {
  id: string;
  label: string;
  description: string;
  score: number;
}

export interface SettingsSearchGroupResult {
  id: string;
  title: string;
  description: string;
  productArea: SettingProductArea;
  fullPageTarget?: SettingGroupFullPageTarget;
  returnsSettingsLocations?: ReturnsSettingsLocations;
  chatSurface?: SettingGroupChatSurface;
  availability?: SettingAvailabilityState;
  settingCount: number;
  score: number;
  matchedSettings: SettingsSearchMatchedSetting[];
}

export interface SettingsSearchCatalogResult {
  query: string;
  coverage: SettingsCatalogCoverage;
  groups: SettingsSearchGroupResult[];
}

export type SettingsSearchDocument =
  | SettingsSearchGroupDocument
  | SettingsSearchSettingDocument;

interface SettingsSearchDocumentBase {
  id: string;
  title: string;
  description: string;
  synonyms?: readonly string[];
  productArea: SettingProductArea;
  group: SettingGroupDefinition;
  groupTitle: string;
  groupDescription: string;
  groupSynonyms?: readonly string[];
  definitions: readonly SettingDefinition[];
  availabilityState: SettingAvailabilityState;
  valueSemantics?: string;
  changeGuidance?: string;
  dependencies?: string;
  valueSource?: string;
}

export interface SettingsSearchGroupDocument extends SettingsSearchDocumentBase {
  kind: "group";
}

export interface SettingsSearchSettingDocument extends SettingsSearchDocumentBase {
  kind: "setting";
  definition: SettingDefinition;
}

interface AggregatedSettingsSearchGroup {
  group: SettingGroupDefinition;
  definitions: readonly SettingDefinition[];
  availabilityState: SettingAvailabilityState;
  bestFuseScore: number;
  matchedSettingsById: Map<string, SettingsSearchMatchedSetting>;
}

export function searchSettingsCatalog({
  query,
  productArea,
  limit,
  viewerVisibility = DEFAULT_SETTINGS_SEARCH_VIEWER_VISIBILITY,
  permissions,
  availabilityContext,
}: {
  query: string;
  productArea?: SettingProductArea;
  limit: number;
  viewerVisibility?: SettingVisibility;
  permissions?: readonly string[];
  availabilityContext?: SettingAvailabilityContext;
}): SettingsSearchCatalogResult {
  const sanitizedLimit = sanitizeSearchLimit(limit);
  const trimmedQuery = query.trim();
  const emptyResult = (): SettingsSearchCatalogResult => ({
    query,
    coverage: describeSettingsCatalogCoverage({ productArea, resultCount: 0 }),
    groups: [],
  });
  if (shouldShortCircuitCatalogSearch({ trimmedQuery, sanitizedLimit })) {
    return emptyResult();
  }

  const searchDocuments = createSettingsSearchDocuments({
    viewerVisibility,
    permissions,
    availabilityContext,
  });
  const groupsById = new Map<string, AggregatedSettingsSearchGroup>();
  for (const result of searchWeightedDocuments({
    documents: searchDocuments,
    keys: SETTINGS_SEARCH_KEYS,
    query: trimmedQuery,
    exactMatchKeys: SETTINGS_SEARCH_EXACT_MATCH_KEYS,
  })) {
    const document = result.document;
    const fuseScore = getSettingsDocumentFuseScore(document, result.score);
    const groupResult = getOrCreateAggregatedSearchGroup(
      groupsById,
      document.group,
      document.definitions,
      document.availabilityState,
    );
    groupResult.bestFuseScore = Math.min(groupResult.bestFuseScore, fuseScore);

    if (document.kind === "setting") {
      const metadata = document.definition.metadata;
      const matchedSetting = {
        id: document.definition.id,
        label: metadata.label,
        description: metadata.description,
        score: toSearchRelevanceScore(fuseScore),
      };
      const existing = groupResult.matchedSettingsById.get(matchedSetting.id);
      if (!existing || matchedSetting.score > existing.score) {
        groupResult.matchedSettingsById.set(matchedSetting.id, matchedSetting);
      }
    }
  }

  const groups = [...groupsById.values()]
    .sort((a, b) => a.bestFuseScore - b.bestFuseScore)
    .slice(0, sanitizedLimit)
    .map(
      (result): SettingsSearchGroupResult => ({
        id: result.group.id,
        title: result.group.title,
        description: result.group.description,
        productArea: result.group.productArea,
        fullPageTarget: result.group.fullPageTarget,
        returnsSettingsLocations: getReturnsSettingsLocations({
          groupId: result.group.id,
          context: availabilityContext,
        }),
        chatSurface: result.group.chatSurface,
        ...(result.availabilityState.status === "unavailable"
          ? { availability: result.availabilityState }
          : {}),
        settingCount: result.definitions.length,
        score: toSearchRelevanceScore(result.bestFuseScore),
        matchedSettings: [...result.matchedSettingsById.values()]
          .sort((a, b) => b.score - a.score)
          .slice(0, MAX_MATCHED_SETTINGS_PER_GROUP),
      }),
    );

  return {
    query,
    coverage: describeSettingsCatalogCoverage({
      productArea,
      resultCount: groups.length,
    }),
    groups,
  };
}

function getSettingsDocumentFuseScore(
  document: SettingsSearchDocument,
  score: number | undefined,
): number {
  let fuseScore = score ?? 0;
  if (
    document.kind === "setting" &&
    document.definition.groupId !== document.group.id
  ) {
    fuseScore = Math.min(fuseScore + 0.08, 1);
  }
  return fuseScore;
}

export function createSettingsSearchDocuments({
  viewerVisibility = DEFAULT_SETTINGS_SEARCH_VIEWER_VISIBILITY,
  permissions,
  availabilityContext,
}: {
  viewerVisibility?: SettingVisibility;
  permissions?: readonly string[];
  availabilityContext?: SettingAvailabilityContext;
}): SettingsSearchDocument[] {
  return SettingCatalogGroups.flatMap((group: SettingGroupDefinition) => {
    if (!canReadSetting(viewerVisibility, group.access, permissions)) {
      return [];
    }

    const availabilityState = evaluateSettingAvailability({
      availability: group.availability,
      context: availabilityContext,
      missingContextBehavior: "assumeAvailable",
    });
    if (!isSettingAvailabilitySearchVisible(availabilityState)) {
      return [];
    }

    const definitions = getVisibleDefinitionsForGroup({
      groupId: group.id,
      viewerVisibility,
      permissions,
      availabilityContext,
      groupAvailabilityState: availabilityState,
    });
    // A group that owns settings but exposes none to this viewer stays hidden.
    // A route-anchor group owns none by design, and must stay searchable so
    // Ask Redo can still link its page.
    if (
      definitions.length === 0 &&
      availabilityState.status === "available" &&
      getSettingCatalogDefinitionsForGroup(group.id).length > 0
    ) {
      return [];
    }

    const groupSynonyms = [
      ...(group.synonyms ?? []),
      ...(group.searchLabels ?? []),
    ];
    return [
      {
        kind: "group" as const,
        id: group.id,
        title: group.title,
        description: group.description,
        synonyms: groupSynonyms,
        productArea: group.productArea,
        group,
        groupTitle: group.title,
        groupDescription: group.description,
        groupSynonyms,
        definitions,
        availabilityState,
      },
      ...definitions.map((definition) => {
        const metadata = definition.metadata;
        return {
          kind: "setting" as const,
          id: definition.id,
          title: metadata.label,
          description: metadata.description,
          synonyms: [
            ...(metadata.synonyms ?? []),
            ...(metadata.searchLabels ?? []),
          ],
          productArea: metadata.productArea,
          group,
          groupTitle: group.title,
          groupDescription: group.description,
          groupSynonyms: group.synonyms,
          definitions,
          availabilityState,
          definition,
          valueSemantics: createValueSemanticsSearchText(
            metadata.valueSemantics,
          ),
          changeGuidance: stringifySearchContext(metadata.changeGuidance),
          dependencies: stringifySearchContext(metadata.dependencies),
          valueSource: stringifySearchContext(definition.valueSource),
        };
      }),
    ];
  });
}

function getVisibleDefinitionsForGroup({
  groupId,
  viewerVisibility,
  permissions,
  availabilityContext,
  groupAvailabilityState,
}: {
  groupId: string;
  viewerVisibility: SettingVisibility;
  permissions?: readonly string[];
  availabilityContext?: SettingAvailabilityContext;
  groupAvailabilityState: SettingAvailabilityState;
}): readonly SettingDefinition[] {
  if (!shouldShowSettingsForAvailability(groupAvailabilityState)) {
    return [];
  }

  return getSettingCatalogDefinitionsForGroup(groupId).filter((definition) => {
    if (
      !canReadSetting(viewerVisibility, definition.metadata.access, permissions)
    ) {
      return false;
    }

    return isSettingAvailabilitySearchVisible(
      evaluateSettingAvailability({
        availability: definition.metadata.availability,
        context: availabilityContext,
        missingContextBehavior: "assumeAvailable",
      }),
    );
  });
}

function getOrCreateAggregatedSearchGroup(
  groupsById: Map<string, AggregatedSettingsSearchGroup>,
  group: SettingGroupDefinition,
  definitions: readonly SettingDefinition[],
  availabilityState: SettingAvailabilityState,
): AggregatedSettingsSearchGroup {
  const existing = groupsById.get(group.id);
  if (existing) {
    return existing;
  }

  const created = {
    group,
    definitions,
    availabilityState,
    bestFuseScore: Number.POSITIVE_INFINITY,
    matchedSettingsById: new Map<string, SettingsSearchMatchedSetting>(),
  };
  groupsById.set(group.id, created);
  return created;
}
